import {
    OnInit,
    NgZone,
    ChangeDetectorRef,
    Component,
    Input,
    Output,
    EventEmitter
} from '@angular/core';
import { ArrayDataSource } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { finalize } from 'rxjs/operators';
import { Map, FeatureGroup, CircleMarker, Polygon, latLngBounds, Polyline } from 'leaflet';
// Services
import { AuthHttpService } from 'src/app/services/auth-http.service';
import { RegionMapsService } from 'src/app/services/region-maps.service';
import { CoreService } from 'src/app/services/core.service';
// Models
import { FlatPathNode, Region, COMMON, COMMONTRACK, TrackSeqRef, RegionRef, LocationRef, TrackSeq, Location } from '@mocca-technologies/model';
import { HttpErrorResponse } from '@angular/common/http';

class TreeData {
    treeData: FlatPathNode[] = [];
    dataSource: ArrayDataSource<FlatPathNode> = null;
    treeControl = new FlatTreeControl<FlatPathNode>(
        (node) => node.level,
        (node) => node.expandable
    );

    constructor() {}

    hasChild = (_: number, node: FlatPathNode) => node.expandable;

    expandNode = (node: FlatPathNode) => (node.isExpanded = !node.isExpanded);

    getParentNode(node: FlatPathNode): FlatPathNode | null {
        const nodeIndex = this.treeData.indexOf(node);

        for (let i = nodeIndex - 1; i >= 0; i--) {
            if (this.treeData[i].level === node.level - 1) {
                return this.treeData[i];
            }
        }

        return null;
    }

    shouldRender(node: FlatPathNode): string {
        if (!node.matchFilter) {
            return 'none';
        }

        let parent = this.getParentNode(node);

        while (parent) {
            if (!parent.isExpanded) {
                return 'none';
            }
            parent = this.getParentNode(parent);
        }

        return 'flex';
    }
}

@Component({
    selector: 'app-region-side',
    templateUrl: './region-side.component.html',
    styleUrls: ['./region-side.component.scss']
})
export class RegionSideComponent extends TreeData implements OnInit {
    private selectCounter = 0;

    // Leaflet ref
    @Input() leafletMap: Map;

    // Input from parent
    @Input() viewLayer: FeatureGroup;

    // Output events
    @Output() onToggleDetails: EventEmitter<boolean> = new EventEmitter();
    @Output() onCreateClick: EventEmitter<void> = new EventEmitter();

    constructor(
        private zone: NgZone,
        private ref: ChangeDetectorRef,
        public accountService: AuthHttpService,
        public regionMapsService: RegionMapsService,
        private coreService: CoreService
    ) {
        super();
    }

    ngOnInit() {
        this.refreshRegionsList();
    }

    /* UI Logic */
    selectRegionGroup(node: FlatPathNode) {
        this.onToggleDetails.emit(true);
        this.regionMapsService.selectRegionGroup(node);
    }

    refreshRegionsList(event: CustomEvent | { target: any } = null) {
        this.regionMapsService
            .getFlattenedList()
            .pipe(finalize(() => (event as any)?.target.complete()))
            .subscribe({
              next: (data) => {
                this.treeData = data;
                this.dataSource = new ArrayDataSource(data);
              },
              error: (error: HttpErrorResponse) => {
                this.coreService.showServerResponse(error?.status, error?.error);
              }
            });
    }

    refreshInterface(node: FlatPathNode) {
        const setupLeafletLayer = (item: RegionRef | LocationRef | TrackSeqRef, node: FlatPathNode): void => {
            const polygonLayer = (item as RegionRef).region?.polygonLayer || 
                                (item as LocationRef).location?.polygonLayer || 
                                (item as TrackSeqRef).trackSequence?.polygonLayer;
            
            if (!polygonLayer) return;
            
            polygonLayer.setStyle({
                ...COMMON,
                color: this.regionMapsService.getHexColor(node.dataRef.color)
            });
            polygonLayer
                .bindTooltip((item as RegionRef).regionName || (item as LocationRef).locationName || (item as TrackSeqRef).trackName)
                .setStyle({ interactive: true });

            // Events
            polygonLayer.on('click', () => {
                this.zone.run(() => this.selectRegionGroup(node));
            });
        };

        const setupLeafletLayerTracks = (item: RegionRef | LocationRef | TrackSeqRef, node: FlatPathNode): void => {
            const polygonLayer = (item as RegionRef).region?.polygonLayer || 
                                (item as LocationRef).location?.polygonLayer || 
                                (item as TrackSeqRef).trackSequence?.polygonLayer;
            
            if (!polygonLayer) return;
            
            polygonLayer.setStyle({
                ...COMMONTRACK,
                color: this.regionMapsService.getHexColor(node.dataRef.color)
            });
            polygonLayer
                .bindTooltip((item as RegionRef).regionName || (item as LocationRef).locationName || (item as TrackSeqRef).trackName)
                .setStyle({ interactive: true });

            // Events
            polygonLayer.on('click', () => {
                this.zone.run(() => this.selectRegionGroup(node));
            });
        };

        const checked = node.dataRef.isChecked;

        if (checked) {
            node.dataRef.color = this.regionMapsService.getNameColor(this.selectCounter);
            node.dataRef.regions.forEach((region) => setupLeafletLayer(region, node));
            node.dataRef.locations.forEach((location) => setupLeafletLayerTracks(location, node));
            node.dataRef.tracks.forEach((track) => setupLeafletLayerTracks(track, node))
            this.selectCounter++;
        } else {
            node.dataRef.color = 'light';
        }

        // Set Badge count
        let parent = this.getParentNode(node);
        while (parent) {
            if (checked) {
                parent.badgeValue++;
            } else {
                parent.badgeValue--;
            }
            parent = this.getParentNode(parent);
        }
    }

    drawOnMap(node: FlatPathNode) {
        // setTimeout moves this function at the top of the stack.
        setTimeout(() => {
            node.dataRef.regions.forEach((regionRef: RegionRef) => {
                regionRef.region.isChecked = node.dataRef.isChecked;
                regionRef.region.isChecked
                    ? this.viewLayer.addLayer(regionRef?.region?.polygonLayer)
                    : this.viewLayer.removeLayer(regionRef?.region?.polygonLayer);
            });
            node.dataRef.locations.forEach((locationRef: LocationRef) => {
                locationRef.location.isChecked = node?.dataRef?.isChecked;
                locationRef.location.isChecked
                    ? this.viewLayer.addLayer(locationRef?.location?.polygonLayer)
                    : this.viewLayer.removeLayer(locationRef?.location?.polygonLayer);
            });

            node.dataRef.tracks.forEach((trackRef: TrackSeqRef) => {
                trackRef.trackSequence.isChecked = node?.dataRef?.isChecked;
                trackRef.trackSequence.isChecked
                    ? this.viewLayer.addLayer(trackRef?.trackSequence?.polygonLayer)
                    : this.viewLayer.removeLayer(trackRef?.trackSequence?.polygonLayer);
            });

            // Call change detection
            this.ref.markForCheck();
        });
    }

    filterRender(ev: Event) {
        const search = (ev as CustomEvent).detail.value.toLowerCase();

        // Filter to check only leaf nodes (aka Region Groups, not directories)
        this.treeData.forEach((node) => (node.matchFilter = false));

        // Check if string is included into each path, then apply the same visibility onto parents
        this.treeData
            .filter((el) => !el.expandable)
            .forEach((node) => {
                const path = node.dataRef.path.toLowerCase();
                const includes = path.includes(search);
                node.matchFilter = includes;

                let parent = this.getParentNode(node);
                while (parent) {
                    parent.matchFilter = parent.matchFilter || includes;
                    parent = this.getParentNode(parent);
                }
            });
    }

    /* Footer */
    fitCamera() {
        const bounds = latLngBounds([]);

        this.viewLayer.eachLayer((layer: Polygon | CircleMarker | Polyline) => {
            if (layer instanceof Polygon) {
                bounds.extend(layer.getBounds());
            } else if (layer instanceof CircleMarker) {
                bounds.extend(layer.getLatLng());
            } else if (layer instanceof Polyline) {
                bounds.extend(layer.getCenter());
            }
        });

        bounds.isValid() && this.leafletMap.fitBounds(bounds);
    }

    fireCreateEvent() {
        this.onToggleDetails.emit(false);
        this.onCreateClick.emit();
    }
}
